import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFlags, useRole, useUser } from '@/stores/identity';
import {
  listPharmacistOnDutyGaps,
  type PharmacistOnDutyGap,
} from '@/lib/scheduling';
import {
  transitionTimeOff,
  formatTimeOffRange,
  timeOffDayCount,
} from '@/lib/timeOff';
import { listAuditEvents } from '@/lib/audit';
import { formatDate } from '@/lib/dates';
import type {
  AuditEvent,
  Location,
  Schedule,
  Shift,
  Staff,
  TimeOffRequest,
} from '@/lib/types';

/**
 * Manager Reports page (Day 15.x — Reporting Phase 1, Manager surface).
 *
 * Six operational tiles, all scoped to a selected period (single month or
 * full year). Top-of-page time selector lets the manager browse history or
 * look ahead while planning. RLS scopes data to the user's location(s).
 */

type ViewMode = 'month' | 'year';

interface PeriodRange {
  start: string;          // YYYY-MM-DD inclusive
  end: string;            // YYYY-MM-DD inclusive
  monthStarts: string[];  // YYYY-MM-DD for each month in the period
  label: string;          // human-friendly label
}

interface ManagerData {
  locations: Location[];
  staff: Staff[];
  shifts: Shift[];
  schedules: Schedule[];
  timeOff: TimeOffRequest[];
  gapsByLocation: Array<{ locationId: string; locationName: string; gaps: PharmacistOnDutyGap[] }>;
  recentChanges: AuditEvent[];
}

function shiftHours(s: Shift): number {
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  mins -= s.break_minutes ?? 0;
  return Math.max(0, mins) / 60;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function buildPeriod(viewMode: ViewMode, year: number, month: number): PeriodRange {
  if (viewMode === 'year') {
    const monthStarts = Array.from({ length: 12 }, (_, m) => `${year}-${pad2(m + 1)}-01`);
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`,
      monthStarts,
      label: String(year),
    };
  }
  const start = `${year}-${pad2(month + 1)}-01`;
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0).getDate();
  const end = `${year}-${pad2(month + 1)}-${pad2(lastDay)}`;
  const label = new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  return { start, end, monthStarts: [start], label };
}

/**
 * Convert a per-week or per-month staff target into the equivalent target
 * for the selected period (single month vs full year). Reports normalize
 * across mixed-cadence staff.
 */
function periodTarget(
  perPeriod: number,
  period: 'week' | 'month',
  range: PeriodRange,
  monthsInPeriod: number
): number {
  if (period === 'month') return perPeriod * monthsInPeriod;
  // Weekly target × weeks in the range. ~365.25/12 ≈ 30.4 days/month so 4.35 weeks.
  // For full year: 52 weeks. For one month: ~4.35 weeks.
  const startD = new Date(range.start + 'T00:00:00');
  const endD = new Date(range.end + 'T00:00:00');
  const days = Math.round((endD.getTime() - startD.getTime()) / 86400000) + 1;
  return Math.round(perPeriod * (days / 7));
}

export default function ManagerReports() {
  const role = useRole();
  const user = useUser();
  const flags = useFlags();
  const isPlatformAdmin = Boolean(flags?.is_platform_admin);
  const today = useMemo(() => new Date(), []);

  // Time selection
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedYear, setSelectedYear] = useState<number>(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(today.getMonth());
  const period = useMemo(
    () => buildPeriod(viewMode, selectedYear, selectedMonth),
    [viewMode, selectedYear, selectedMonth]
  );
  const monthsInPeriod = period.monthStarts.length;

  const [initialStatus, setInitialStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('all');
  const [data, setData] = useState<ManagerData>({
    locations: [],
    staff: [],
    shifts: [],
    schedules: [],
    timeOff: [],
    gapsByLocation: [],
    recentChanges: [],
  });
  const [decisionInFlight, setDecisionInFlight] = useState<string | null>(null);

  /**
   * Period-scoped data — runs whenever the period changes. Locations + staff
   * are passed in so we don't refetch them every period change.
   */
  async function loadPeriodData(locs: Location[]) {
    // All five queries run in parallel.
    const shiftsP = supabase
      .from('shifts')
      .select('*')
      .gte('shift_date', period.start)
      .lte('shift_date', period.end)
      .is('cancelled_at', null);

    const schedulesP = supabase
      .from('schedules')
      .select('*')
      .in('month_start_date', period.monthStarts);

    const timeOffP = supabase
      .from('time_off_requests')
      .select('*')
      .lte('start_date', period.end)
      .gte('end_date', period.start)
      .order('start_date');

    const gapsP = Promise.all(
      locs.map(async (loc) => {
        const results = await Promise.allSettled(
          period.monthStarts.map((ms) => listPharmacistOnDutyGaps(loc.id, ms))
        );
        const merged = results
          .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
          .sort((a, b) =>
            a.gap_date.localeCompare(b.gap_date) || a.gap_start.localeCompare(b.gap_start)
          );
        return { locationId: loc.id, locationName: loc.name, gaps: merged };
      })
    );

    const auditP = listAuditEvents(200, 0, null);

    const [shiftsRes, schedRes, toRes, gapsRes, auditRes] = await Promise.all([
      shiftsP,
      schedulesP,
      timeOffP,
      gapsP,
      auditP,
    ]);

    if (shiftsRes.error) throw shiftsRes.error;
    if (schedRes.error) throw schedRes.error;
    if (toRes.error) throw toRes.error;

    const recentChanges = auditRes.rows
      .filter(
        (r) =>
          r.event_type.startsWith('SCHEDULE_') ||
          r.event_type.startsWith('SHIFT_') ||
          r.event_type.startsWith('DAY_ANNOTATION_')
      )
      .filter((r) => {
        const d = r.occurred_at.slice(0, 10);
        return d >= period.start && d <= period.end;
      })
      .slice(0, 12);

    return {
      shifts: (shiftsRes.data ?? []) as Shift[],
      schedules: (schedRes.data ?? []) as Schedule[],
      timeOff: (toRes.data ?? []) as TimeOffRequest[],
      gapsByLocation: gapsRes,
      recentChanges,
    };
  }

  /**
   * Initial mount: load shell (locations + staff) AND first period in parallel.
   * Shell is then cached and reused for subsequent period changes.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [locsRes, staffRes, mgrRes] = await Promise.all([
          supabase.from('locations').select('*').eq('status', 'active').order('name'),
          supabase.from('staff').select('*').is('active_to', null),
          // Manager-scope: only the locations this user is the manager of.
          // Owners and platform admins skip this filter (see below).
          user?.id
            ? supabase.from('location_managers').select('location_id').eq('user_id', user.id)
            : Promise.resolve({ data: [], error: null }),
        ]);
        if (locsRes.error) throw locsRes.error;
        if (staffRes.error) throw staffRes.error;
        if (mgrRes.error) throw mgrRes.error;

        const allLocations = (locsRes.data ?? []) as Location[];
        const staff = (staffRes.data ?? []) as Staff[];
        const managedIds = new Set(
          ((mgrRes.data ?? []) as Array<{ location_id: string }>).map((r) => r.location_id)
        );

        // Managers see only locations they manage.
        // Owners and platform admins see all org locations (read-everywhere by design).
        const locations =
          role === 'manager' && !isPlatformAdmin
            ? allLocations.filter((l) => managedIds.has(l.id))
            : allLocations;

        // If a manager has exactly one managed location, default the picker to it.
        if (role === 'manager' && !isPlatformAdmin && locations.length === 1) {
          setSelectedLocationId(locations[0].id);
        }

        const periodSlice = await loadPeriodData(locations);
        if (cancelled) return;

        setData({ locations, staff, ...periodSlice });
        setInitialStatus('loaded');
      } catch (err) {
        if (cancelled) return;
        // Surface via the page banner; setErrMsg already includes err.message.
        setErrMsg((err as Error).message);
        setInitialStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Period changes (after initial load): refresh only the period slice.
   * Keep showing previous data while loading — no full-page blank.
   */
  useEffect(() => {
    if (initialStatus !== 'loaded') return;
    let cancelled = false;
    setRefreshing(true);
    (async () => {
      try {
        const periodSlice = await loadPeriodData(data.locations);
        if (cancelled) return;
        setData((prev) => ({ ...prev, ...periodSlice }));
      } catch (err) {
        if (cancelled) return;
        // non-critical: period-switch refresh failed. Keep prior data on screen;
        // user can re-pick the period to retry. Don't tear the page down.
        console.warn('ManagerReports period reload failed', err);
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end]);

  // ---- navigation ----------------------------------------------------------

  function goPrev() {
    if (viewMode === 'year') {
      setSelectedYear((y) => y - 1);
    } else if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonth((m) => m - 1);
    }
  }
  function goNext() {
    if (viewMode === 'year') {
      setSelectedYear((y) => y + 1);
    } else if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonth((m) => m + 1);
    }
  }
  function jumpToToday() {
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
  }

  // ---- filter by selected location -----------------------------------------

  const visibleLocationIds = useMemo(() => {
    if (selectedLocationId === 'all') return data.locations.map((l) => l.id);
    return [selectedLocationId];
  }, [selectedLocationId, data.locations]);

  const filteredStaff = useMemo(
    () =>
      data.staff.filter(
        (s) => !s.primary_location_id || visibleLocationIds.includes(s.primary_location_id)
      ),
    [data.staff, visibleLocationIds]
  );
  const filteredShifts = useMemo(
    () => data.shifts.filter((s) => visibleLocationIds.includes(s.location_id)),
    [data.shifts, visibleLocationIds]
  );
  const filteredGaps = useMemo(
    () => data.gapsByLocation.filter((g) => visibleLocationIds.includes(g.locationId)),
    [data.gapsByLocation, visibleLocationIds]
  );
  const filteredSchedules = useMemo(
    () => data.schedules.filter((s) => visibleLocationIds.includes(s.location_id)),
    [data.schedules, visibleLocationIds]
  );
  const filteredTimeOff = useMemo(() => {
    const staffIds = new Set(filteredStaff.map((s) => s.id));
    return data.timeOff.filter((t) => staffIds.has(t.staff_id));
  }, [data.timeOff, filteredStaff]);
  const pendingTimeOff = useMemo(
    () => filteredTimeOff.filter((t) => t.status === 'pending'),
    [filteredTimeOff]
  );

  // ---- derived ------------------------------------------------------------

  const totalGaps = filteredGaps.reduce((sum, g) => sum + g.gaps.length, 0);
  const periodHours = filteredShifts.reduce((sum, s) => sum + shiftHours(s), 0);

  const hoursByStaff = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of filteredShifts) {
      if (!s.staff_id) continue;
      map.set(s.staff_id, (map.get(s.staff_id) ?? 0) + shiftHours(s));
    }
    return map;
  }, [filteredShifts]);

  const fairnessByStaff = useMemo(() => {
    const map = new Map<string, { weekend: number; evening: number }>();
    for (const s of filteredShifts) {
      if (!s.staff_id) continue;
      const d = new Date(s.shift_date + 'T00:00:00');
      const dow = d.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const startHour = parseInt(s.start_time.split(':')[0], 10);
      const isEvening = startHour >= 16;
      const cur = map.get(s.staff_id) ?? { weekend: 0, evening: 0 };
      map.set(s.staff_id, {
        weekend: cur.weekend + (isWeekend ? 1 : 0),
        evening: cur.evening + (isEvening ? 1 : 0),
      });
    }
    return map;
  }, [filteredShifts]);

  const fairnessAvg = useMemo(() => {
    const arr = Array.from(fairnessByStaff.values());
    if (arr.length === 0) return { weekend: 0, evening: 0 };
    const wk = arr.reduce((a, b) => a + b.weekend, 0) / arr.length;
    const ev = arr.reduce((a, b) => a + b.evening, 0) / arr.length;
    return { weekend: wk, evening: ev };
  }, [fairnessByStaff]);

  // ---- actions ------------------------------------------------------------

  async function decide(reqId: string, to: 'approved' | 'denied') {
    setDecisionInFlight(reqId);
    try {
      await transitionTimeOff(reqId, to, null);
      // Reload only the time_off slice
      const { data: toRows, error } = await supabase
        .from('time_off_requests')
        .select('*')
        .lte('start_date', period.end)
        .gte('end_date', period.start)
        .order('start_date');
      if (error) throw error;
      setData((d) => ({ ...d, timeOff: (toRows ?? []) as TimeOffRequest[] }));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setDecisionInFlight(null);
    }
  }

  // ---- render -------------------------------------------------------------

  if (initialStatus === 'loading') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading reports...
      </div>
    );
  }
  if (initialStatus === 'error') {
    return (
      <div className="container mx-auto py-10 px-4 text-center text-sm text-red-700">
        Couldn't load reports: {errMsg ?? 'unknown error'}
      </div>
    );
  }

  const titleScope = role === 'owner' ? 'Owner / manager view' : 'Manager view';
  const isCurrentPeriod =
    viewMode === 'month'
      ? selectedYear === today.getFullYear() && selectedMonth === today.getMonth()
      : selectedYear === today.getFullYear();

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header + period selector + filter */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">{titleScope}</p>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              Reports
              {refreshing && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                  aria-live="polite"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  Updating...
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">{period.label}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5 bg-card">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous period"
                className="rounded px-2 py-1 text-sm hover:bg-muted/40"
              >
                ◀
              </button>
              <span className="px-2 text-sm font-medium tabular-nums w-[150px] text-center inline-block">
                {period.label}
              </span>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next period"
                className="rounded px-2 py-1 text-sm hover:bg-muted/40"
              >
                ▶
              </button>
            </div>
            {/* K-10 fix: always render the Today button — disable it when
                on the current period — so toggling on/off doesn't reflow the
                row width as the operator clicks ← → through months. */}
            <button
              type="button"
              onClick={jumpToToday}
              disabled={isCurrentPeriod}
              aria-disabled={isCurrentPeriod}
              className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/40 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent"
            >
              Today
            </button>
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5 bg-card">
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={
                  viewMode === 'month'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground'
                    : 'rounded px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/40'
                }
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setViewMode('year')}
                className={
                  viewMode === 'year'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground'
                    : 'rounded px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/40'
                }
              >
                Year
              </button>
            </div>
            {data.locations.length > 1 ? (
              <div className="flex items-center gap-2">
                <label htmlFor="loc-filter" className="text-xs text-muted-foreground">
                  Location
                </label>
                <select
                  id="loc-filter"
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                >
                  <option value="all">All ({data.locations.length})</option>
                  {data.locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            ) : data.locations.length === 1 ? (
              <span className="text-xs text-muted-foreground">
                {data.locations[0].name}
              </span>
            ) : null}
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="Coverage gaps"
            value={String(totalGaps)}
            sub={totalGaps > 0 ? `in ${period.label}` : 'all clear'}
            tone={totalGaps > 0 ? 'warning' : 'positive'}
          />
          <KpiCard
            label="Pending time-off"
            value={String(pendingTimeOff.length)}
            sub={pendingTimeOff.length > 0 ? 'awaiting decision' : 'all decided'}
            tone={pendingTimeOff.length > 0 ? 'warning' : 'positive'}
          />
          <KpiCard
            label="Hours scheduled"
            value={`${periodHours.toFixed(0)} h`}
            sub={`${filteredStaff.length} staff`}
          />
          <KpiCard
            label="Schedule mode"
            value={viewMode === 'month' ? 'Month' : 'Year'}
            sub={viewMode === 'year' ? `${monthsInPeriod} months` : period.label}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 01 — Coverage gaps */}
          <Tile id="01" label="Action needed" title={`Coverage gaps · ${period.label}`}>
            {(() => {
              const allGaps = filteredGaps
                .flatMap((loc) =>
                  loc.gaps.map((g) => ({
                    ...g,
                    locationId: loc.locationId,
                    locationName: loc.locationName,
                  }))
                )
                .sort((a, b) =>
                  a.gap_date.localeCompare(b.gap_date) || a.gap_start.localeCompare(b.gap_start)
                );
              if (allGaps.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground">
                    No pharmacist coverage gaps in {period.label}. Nice.
                  </p>
                );
              }
              const visible = viewMode === 'year' ? allGaps.slice(0, 12) : allGaps.slice(0, 8);
              return (
                <>
                  <ul className="space-y-1.5 text-sm">
                    {visible.map((g, i) => (
                      <li
                        key={`${g.locationId}-${g.gap_date}-${i}`}
                        className="flex justify-between items-center px-3 py-1.5 rounded-md bg-red-50"
                      >
                        <span>
                          <span className="font-medium text-red-800">{formatDate(g.gap_date)}</span>
                          <span className="text-xs text-red-700 ml-2">
                            {g.gap_start.slice(0, 5)} – {g.gap_end.slice(0, 5)}
                          </span>
                        </span>
                        {data.locations.length > 1 && (
                          <span className="text-[10px] text-red-700">{g.locationName}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {allGaps.length > visible.length && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Showing {visible.length} of {allGaps.length} — open the schedule for full view.
                    </p>
                  )}
                </>
              );
            })()}
          </Tile>

          {/* 02 — Time-off pipeline */}
          <Tile id="02" label="Pending" title={`Time-off · ${period.label}`}>
            {filteredTimeOff.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No time-off requests overlapping {period.label}.
              </p>
            ) : (
              <>
                {pendingTimeOff.length > 0 && (
                  <ul className="space-y-2 text-sm mb-2">
                    {pendingTimeOff.slice(0, 4).map((r) => {
                      const staff = filteredStaff.find((s) => s.id === r.staff_id);
                      return (
                        <li key={r.id} className="rounded-md border border-border p-2">
                          <div className="flex justify-between items-start gap-2">
                            <div>
                              <p className="font-medium">{staff?.display_name ?? 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatTimeOffRange(r)} · {timeOffDayCount(r)}d · {r.type}
                              </p>
                              {r.reason && (
                                <p className="text-xs text-muted-foreground italic mt-1">{r.reason}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                disabled={decisionInFlight === r.id}
                                onClick={() => decide(r.id, 'approved')}
                                className="rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={decisionInFlight === r.id}
                                onClick={() => decide(r.id, 'denied')}
                                className="rounded-md border border-red-300 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                              >
                                Deny
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-md bg-amber-50 px-2 py-1.5">
                    <p className="font-semibold text-amber-800">{pendingTimeOff.length}</p>
                    <p className="text-[10px] text-muted-foreground">Pending</p>
                  </div>
                  <div className="rounded-md bg-emerald-50 px-2 py-1.5">
                    <p className="font-semibold text-emerald-800">
                      {filteredTimeOff.filter((r) => r.status === 'approved').length}
                    </p>
                    <p className="text-[10px] text-muted-foreground">Approved</p>
                  </div>
                  <div className="rounded-md bg-muted/40 px-2 py-1.5">
                    <p className="font-semibold">
                      {filteredTimeOff.filter((r) => r.status === 'denied').length}
                    </p>
                    <p className="text-[10px] text-muted-foreground">Denied</p>
                  </div>
                </div>
              </>
            )}
          </Tile>

          {/* 03 — Hours over period vs target */}
          <Tile id="03" label="Utilization" title={`Hours in ${period.label}`}>
            {filteredStaff.length === 0 ? (
              <p className="text-sm text-muted-foreground">No staff at this location.</p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {filteredStaff.slice(0, 8).map((s) => {
                  const hrs = hoursByStaff.get(s.id) ?? 0;
                  const target = {
                    min: periodTarget(Number(s.min_hours), s.hours_target_period, period, monthsInPeriod),
                    max: periodTarget(Number(s.max_hours), s.hours_target_period, period, monthsInPeriod),
                  };
                  const pct = Math.min(100, (hrs / Math.max(target.max, 1)) * 100);
                  const tone =
                    hrs > target.max ? 'bg-red-500' :
                    hrs < target.min ? 'bg-amber-400' :
                    'bg-emerald-500';
                  return (
                    <li key={s.id} className="flex items-center gap-2">
                      <span className="w-24 truncate text-muted-foreground">{s.display_name}</span>
                      <div className="flex-1 h-2 bg-muted rounded">
                        <div className={`h-full rounded ${tone}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-24 text-right tabular-nums">
                        {hrs.toFixed(0)}/{target.max}h
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {filteredStaff.length > 8 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Showing 8 of {filteredStaff.length}.
              </p>
            )}
          </Tile>

          {/* 04 — Shift fairness */}
          <Tile id="04" label="Equity" title={`Shift fairness · ${period.label}`}>
            {filteredStaff.length === 0 ? (
              <p className="text-sm text-muted-foreground">No staff to measure.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal pb-1">Staff</th>
                    <th className="text-right font-normal pb-1">Wknd</th>
                    <th className="text-right font-normal pb-1">Eve</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.slice(0, 8).map((s) => {
                    const f = fairnessByStaff.get(s.id) ?? { weekend: 0, evening: 0 };
                    const wkHigh = f.weekend > fairnessAvg.weekend * 1.4;
                    const evHigh = f.evening > fairnessAvg.evening * 1.4;
                    return (
                      <tr key={s.id}>
                        <td className="py-0.5 truncate max-w-[140px]">{s.display_name}</td>
                        <td className={`text-right tabular-nums ${wkHigh ? 'text-red-700 font-medium' : ''}`}>{f.weekend}</td>
                        <td className={`text-right tabular-nums ${evHigh ? 'text-red-700 font-medium' : ''}`}>{f.evening}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <p className="mt-2 text-[10px] text-muted-foreground">
              Red = more than 40% above team average for the period.
            </p>
          </Tile>

          {/* 05 — Schedule status */}
          <Tile id="05" label="Operations" title="Schedule status">
            {viewMode === 'year' ? (
              <YearScheduleGrid
                locations={data.locations.filter((l) => visibleLocationIds.includes(l.id))}
                schedules={filteredSchedules}
                monthStarts={period.monthStarts}
              />
            ) : (
              <MonthScheduleList
                locations={data.locations.filter((l) => visibleLocationIds.includes(l.id))}
                schedules={filteredSchedules}
                periodMonthStart={period.monthStarts[0]}
              />
            )}
          </Tile>

          {/* 06 — Recent changes */}
          <Tile id="06" label="Trail" title={`Recent changes · ${period.label}`}>
            {data.recentChanges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No edits in this period.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {data.recentChanges.map((e) => (
                  <li key={e.event_id} className="flex justify-between gap-2">
                    <span className="truncate">{e.summary}</span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {new Date(e.occurred_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/audit" className="mt-2 inline-block text-xs text-primary underline">
              Full audit log →
            </Link>
          </Tile>
        </div>
      </div>
    </div>
  );
}

// ---------- subcomponents -------------------------------------------------

function KpiCard({
  label, value, sub, tone = 'neutral',
}: { label: string; value: string; sub?: string; tone?: 'neutral' | 'positive' | 'warning' }) {
  const cls =
    tone === 'positive' ? 'bg-emerald-50 text-emerald-900' :
    tone === 'warning'  ? 'bg-amber-50 text-amber-900' :
    'bg-muted/40';
  return (
    <div className={`rounded-md px-3 py-2 ${cls}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-0.5 capitalize">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Tile({
  id, label, title, children,
}: { id: string; label: string; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {id} · {label}
      </span>
      <h2 className="text-sm font-semibold">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function ScheduleStatusPill({
  status, deadline,
}: { status?: 'draft' | 'published' | 'closed'; deadline?: string }) {
  if (!status) {
    return (
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        not started{deadline ? ` · by ${formatDate(deadline)}` : ''}
      </span>
    );
  }
  const cfg = {
    published: 'bg-emerald-600 text-white',
    draft: 'bg-amber-600 text-white',
    closed: 'bg-muted-foreground text-white',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg[status]}`}>
      {status}{deadline && status === 'draft' ? ` · by ${formatDate(deadline)}` : ''}
    </span>
  );
}

function MonthScheduleList({
  locations, schedules, periodMonthStart,
}: { locations: Location[]; schedules: Schedule[]; periodMonthStart: string }) {
  // For month mode: show selected month + immediate next month for each location
  const periodDate = new Date(periodMonthStart + 'T00:00:00');
  const nextMonthDate = new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 1);
  const nextStart = `${nextMonthDate.getFullYear()}-${pad2(nextMonthDate.getMonth() + 1)}-01`;
  const periodLabel = periodDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const nextLabel = nextMonthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <ul className="space-y-2 text-sm">
      {locations.map((l) => {
        const cur = schedules.find((s) => s.location_id === l.id && s.month_start_date === periodMonthStart);
        const nxt = schedules.find((s) => s.location_id === l.id && s.month_start_date === nextStart);
        const lead = l.schedule_publish_lead_days ?? 10;
        const next = new Date(nextStart + 'T00:00:00');
        next.setDate(next.getDate() - lead);
        const deadline = next.toISOString().slice(0, 10);
        return (
          <li key={l.id} className="rounded-md border border-border p-2">
            <p className="font-medium text-xs mb-1">{l.name}</p>
            <div className="flex justify-between items-center text-xs">
              <span>{periodLabel}</span>
              <ScheduleStatusPill status={cur?.status} />
            </div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span>{nextLabel}</span>
              <ScheduleStatusPill status={nxt?.status} deadline={deadline} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function YearScheduleGrid({
  locations, schedules, monthStarts,
}: { locations: Location[]; schedules: Schedule[]; monthStarts: string[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left font-normal pb-1 pr-2">Location</th>
            {monthStarts.map((ms) => (
              <th key={ms} className="text-center font-normal pb-1 px-0.5">
                {new Date(ms + 'T00:00:00').toLocaleDateString(undefined, { month: 'short' })[0]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {locations.map((l) => (
            <tr key={l.id}>
              <td className="py-1 pr-2 truncate max-w-[100px] text-xs">{l.name}</td>
              {monthStarts.map((ms) => {
                const sched = schedules.find((s) => s.location_id === l.id && s.month_start_date === ms);
                const fill =
                  sched?.status === 'published' ? 'bg-emerald-500' :
                  sched?.status === 'draft' ? 'bg-amber-400' :
                  sched?.status === 'closed' ? 'bg-muted-foreground' :
                  'bg-muted/30';
                const initial = sched?.status?.[0].toUpperCase() ?? '·';
                const title = sched ? sched.status : 'not started';
                return (
                  <td key={ms} className="px-0.5 py-1 text-center">
                    <span
                      className={`inline-flex w-5 h-5 items-center justify-center rounded text-[9px] font-semibold ${fill} ${sched ? 'text-white' : 'text-muted-foreground'}`}
                      title={title}
                    >
                      {initial}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
        <span><span className="inline-block w-2.5 h-2.5 bg-emerald-500 rounded mr-1 align-middle" />Published</span>
        <span><span className="inline-block w-2.5 h-2.5 bg-amber-400 rounded mr-1 align-middle" />Draft</span>
        <span><span className="inline-block w-2.5 h-2.5 bg-muted-foreground rounded mr-1 align-middle" />Closed</span>
        <span><span className="inline-block w-2.5 h-2.5 bg-muted/30 rounded mr-1 align-middle" />Not started</span>
      </div>
    </div>
  );
}
